import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Trash2, Upload, Image as ImageIcon, X, FileText,
  ChevronDown, ChevronUp, AlertCircle, RefreshCw, GripVertical,
} from 'lucide-react';
import api, { API_BASE } from '@/api/client';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MAX_FILE_MB = 8;

// Returns a fully qualified URL to the page image for both admin + public use.
// We append the leaflet's updated_at timestamp as a cache-buster so re-uploading
// a page immediately replaces the thumbnail in the admin UI without a hard reload.
function pageImageUrl(leafletId, pageNumber, bust) {
  const q = bust ? `?v=${encodeURIComponent(bust)}` : '';
  return `${API_BASE}/public/leaflets/${leafletId}/pages/${pageNumber}${q}`;
}


// ---------- Create form ----------

function CreateLeafletForm({ onCreated }) {
  const [form, setForm] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Please enter a title.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/admin/leaflets', {
        title: form.title.trim(),
        description: form.description.trim(),
      });
      toast.success('Leaflet created. Upload the first page to publish it.');
      setForm({ title: '', description: '' });
      onCreated?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create leaflet.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="card-moj p-5 space-y-4" data-testid="leaflet-create-form">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-moj-gold/20 flex items-center justify-center">
          <BookOpen size={18} className="text-moj-gold-deep" />
        </div>
        <div>
          <h3 className="font-serif text-lg text-moj-purple-deep">Create a new leaflet</h3>
          <p className="text-xs text-moj-charcoal/60">Add the title and description first — you can upload pages right after.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label-moj" htmlFor="leaflet-title">Leaflet Title *</label>
          <input id="leaflet-title" required className="input-moj" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Who is Jesus?"
            data-testid="leaflet-title-input"/>
        </div>
        <div>
          <label className="label-moj" htmlFor="leaflet-description">Description</label>
          <input id="leaflet-description" className="input-moj" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="One sentence that invites readers in"
            data-testid="leaflet-description-input"/>
        </div>
      </div>
      <button type="submit" disabled={submitting} className="btn-primary" data-testid="leaflet-create-submit">
        <Plus size={16}/> {submitting ? 'Creating…' : 'Create leaflet'}
      </button>
    </form>
  );
}


// ---------- Per-leaflet page uploader ----------

// Sortable page tile. Wraps the previous PageTile in @dnd-kit/sortable so
// admins can drag by the grip handle (and keyboard-reorder via Space + arrows).
// We render upload / delete controls on the tile but mark them `data-no-dnd`
// so clicking them never starts a drag.
function PageTile({ page, leafletId, bust, onDelete, onReplace, isReordering }) {
  const inputRef = useRef(null);
  const pageNumber = page.page_number;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 20 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? 'ring-2 ring-moj-gold rounded-lg' : ''}`}
      data-testid={`leaflet-page-tile-${pageNumber}`}
    >
      {/* Strict A5 aspect ratio so thumbnails never stretch design elements. */}
      <div className="aspect-[1/1.414] bg-moj-cream-soft rounded-lg overflow-hidden border border-moj-gold/30 flex items-center justify-center relative">
        <img
          src={pageImageUrl(leafletId, pageNumber, bust)}
          alt={`Page ${pageNumber}`}
          className="w-full h-full object-contain bg-white"
          loading="lazy"
          draggable={false}
        />
        {/* Drag handle — only drag-aware element. Covers top strip with hover feedback. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="absolute top-1 left-1 inline-flex items-center justify-center w-7 h-7 rounded bg-white/90 text-moj-charcoal/70 hover:bg-moj-gold hover:text-moj-purple-deep cursor-grab active:cursor-grabbing shadow-sm touch-none select-none"
          aria-label={`Drag to reorder page ${pageNumber}`}
          title="Drag to reorder"
          data-testid={`leaflet-page-drag-${pageNumber}`}
        >
          <GripVertical size={14}/>
        </button>
      </div>
      <div className="mt-1 flex items-center justify-between gap-1 text-[11px] font-semibold text-moj-purple-deep">
        <span>Page {pageNumber}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isReordering}
            className="p-1 rounded hover:bg-moj-cream-soft text-moj-purple-deep disabled:opacity-40"
            title="Replace page"
            data-testid={`leaflet-page-upload-${pageNumber}`}
          >
            <RefreshCw size={14}/>
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isReordering}
            className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-40"
            title="Delete page"
            data-testid={`leaflet-page-delete-${pageNumber}`}
          >
            <Trash2 size={14}/>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onReplace(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>
    </div>
  );
}


function LeafletEditor({ leaflet, onClose, onChanged }) {
  const qc = useQueryClient();
  const { data: full, isLoading, refetch } = useQuery({
    queryKey: ['adm-leaflet', leaflet.id],
    queryFn: async () => (await api.get(`/admin/leaflets/${leaflet.id}`)).data,
  });

  const [newPageNumber, setNewPageNumber] = useState(1);
  const addInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Local optimistic ordering — mirrors `full.pages` but is updated instantly
  // on drop so the admin sees the new order before the API round-trip returns.
  const [localPages, setLocalPages] = useState(null);
  const [reordering, setReordering] = useState(false);

  // Keep local pages in sync whenever the server state changes.
  React.useEffect(() => {
    if (full?.pages) setLocalPages(full.pages);
  }, [full?.pages]);

  React.useEffect(() => {
    // Default new-page number to the next slot after the highest existing page.
    const pages = localPages || full?.pages || [];
    if (pages.length) {
      setNewPageNumber(Math.max(...pages.map((p) => p.page_number)) + 1);
    } else {
      setNewPageNumber(1);
    }
  }, [localPages, full?.pages]);

  const uploadPage = async (pageNumber, file) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_FILE_MB} MB).`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('page_number', String(pageNumber));
      fd.append('file', file);
      await api.post(`/admin/leaflets/${leaflet.id}/pages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Page ${pageNumber} uploaded.`);
      await refetch();
      qc.invalidateQueries({ queryKey: ['adm-leaflets'] });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const deletePage = async (pageNumber) => {
    if (!window.confirm(`Delete page ${pageNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leaflets/${leaflet.id}/pages/${pageNumber}`);
      toast.success(`Page ${pageNumber} deleted.`);
      await refetch();
      qc.invalidateQueries({ queryKey: ['adm-leaflets'] });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  // Drag-and-drop reordering — @dnd-kit sensors (pointer, keyboard, touch).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persistOrder = async (orderedIds) => {
    setReordering(true);
    try {
      const res = await api.post(
        `/admin/leaflets/${leaflet.id}/pages/reorder`,
        { order: orderedIds },
      );
      // Replace local pages with the authoritative server response so page
      // numbers are freshly 1..N and future uploads land in the right slot.
      setLocalPages(res.data.pages);
      toast.success('Pages reordered.');
      qc.invalidateQueries({ queryKey: ['adm-leaflet', leaflet.id] });
      qc.invalidateQueries({ queryKey: ['adm-leaflets'] });
      qc.invalidateQueries({ queryKey: ['pub-leaflets'] });
      qc.invalidateQueries({ queryKey: ['pub-leaflet', leaflet.id] });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reorder failed. Reverting.');
      // Roll back on failure.
      await refetch();
    } finally {
      setReordering(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = localPages || [];
    const oldIndex = current.findIndex((p) => p.id === active.id);
    const newIndex = current.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(current, oldIndex, newIndex);
    // Re-stamp page_number so the UI shows the new positions immediately.
    const optimistic = reordered.map((p, i) => ({ ...p, page_number: i + 1 }));
    setLocalPages(optimistic);
    persistOrder(optimistic.map((p) => p.id));
  };

  if (isLoading || !full) {
    return <div className="p-5 text-sm text-moj-charcoal/60">Loading pages…</div>;
  }

  const bust = full.updated_at;
  const pages = localPages || full.pages || [];

  return (
    <div className="border-t border-moj-gold/20 bg-moj-cream-soft/40 p-5 space-y-5" data-testid={`leaflet-editor-${leaflet.id}`}>
      {pages.length === 0 && (
        <div className="flex items-start gap-2 text-sm text-moj-charcoal bg-moj-gold/10 border border-moj-gold/30 rounded-lg p-3">
          <AlertCircle size={16} className="text-moj-gold-deep flex-shrink-0 mt-0.5"/>
          <span>No pages yet. Upload Page 1 to publish this leaflet on the public gallery.</span>
        </div>
      )}

      {/* Add page row — admins can jump to any page number */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label-moj" htmlFor={`np-${leaflet.id}`}>Next page number</label>
          <input
            id={`np-${leaflet.id}`}
            type="number"
            min={1}
            max={200}
            className="input-moj w-28"
            value={newPageNumber}
            onChange={(e) => setNewPageNumber(Math.max(1, parseInt(e.target.value, 10) || 1))}
            data-testid={`leaflet-next-page-input-${leaflet.id}`}
          />
        </div>
        <button
          type="button"
          onClick={() => addInputRef.current?.click()}
          disabled={uploading || reordering}
          className="btn-primary"
          data-testid={`leaflet-add-page-${leaflet.id}`}
        >
          <Upload size={16}/> {uploading ? 'Uploading…' : `Upload page ${newPageNumber}`}
        </button>
        <input
          ref={addInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadPage(newPageNumber, f);
            e.target.value = '';
          }}
        />
        <div className="text-xs text-moj-charcoal/60">
          JPEG · PNG · WebP · max {MAX_FILE_MB} MB · A5 portrait recommended (e.g. 1240×1754)
        </div>
      </div>

      {/* Reorder hint */}
      {pages.length > 1 && (
        <div className="text-xs text-moj-charcoal/70 flex items-center gap-1.5" data-testid="leaflet-reorder-hint">
          <GripVertical size={12}/>
          Drag the grip handle in the top-left of any page to reorder. Pages will renumber automatically.
          {reordering && <span className="text-moj-purple-deep font-semibold">· Saving…</span>}
        </div>
      )}

      {/* Page grid — sortable */}
      {pages.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={pages.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="leaflet-pages-grid">
              {pages.map((p) => (
                <PageTile
                  key={p.id}
                  page={p}
                  leafletId={leaflet.id}
                  bust={bust}
                  isReordering={reordering}
                  onDelete={() => deletePage(p.page_number)}
                  onReplace={(file) => uploadPage(p.page_number, file)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}


// ---------- List row ----------

function LeafletRow({ leaflet, onDelete, onChanged, isOpen, onToggle }) {
  const confirmDelete = async () => {
    if (!window.confirm(`Delete leaflet "${leaflet.title}"? All ${leaflet.page_count} page(s) will also be removed.`)) return;
    onDelete(leaflet);
  };

  return (
    <div className="card-moj overflow-hidden" data-testid={`leaflet-row-${leaflet.id}`}>
      <div className="flex items-start sm:items-center gap-4 p-4 flex-col sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-start gap-3 text-left flex-1 min-w-0"
          data-testid={`leaflet-row-toggle-${leaflet.id}`}
        >
          <div className="w-10 h-10 rounded-lg bg-moj-purple/10 flex items-center justify-center flex-shrink-0">
            <BookOpen size={18} className="text-moj-purple-deep" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-serif text-lg text-moj-purple-deep truncate">{leaflet.title}</h4>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-moj-gold/20 text-moj-gold-deep">
                <FileText size={10}/> {leaflet.page_count} page{leaflet.page_count === 1 ? '' : 's'}
              </span>
              {leaflet.page_count === 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                  Draft (hidden from public)
                </span>
              )}
            </div>
            {leaflet.description && (
              <p className="text-sm text-moj-charcoal/70 mt-0.5 line-clamp-2">{leaflet.description}</p>
            )}
          </div>
          <div className="text-moj-purple-deep/60 flex-shrink-0">
            {isOpen ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
          </div>
        </button>
        <button
          type="button"
          onClick={confirmDelete}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 self-stretch sm:self-auto justify-center touch-manipulation min-h-[40px]"
          data-testid={`leaflet-delete-${leaflet.id}`}
        >
          <Trash2 size={14}/> Delete
        </button>
      </div>
      {isOpen && (
        <LeafletEditor leaflet={leaflet} onClose={onToggle} onChanged={onChanged} />
      )}
    </div>
  );
}


// ---------- Main tab ----------

export default function LeafletsTab() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);

  const { data: leaflets = [], isLoading, refetch } = useQuery({
    queryKey: ['adm-leaflets'],
    queryFn: async () => (await api.get('/admin/leaflets')).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (leaflet) => (await api.delete(`/admin/leaflets/${leaflet.id}`)).data,
    onSuccess: (_, leaflet) => {
      toast.success(`"${leaflet.title}" deleted.`);
      if (openId === leaflet.id) setOpenId(null);
      qc.invalidateQueries({ queryKey: ['adm-leaflets'] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Delete failed.'),
  });

  return (
    <div className="space-y-6" data-testid="leaflets-tab">
      <CreateLeafletForm onCreated={(l) => { refetch(); setOpenId(l.id); }} />

      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl text-moj-purple-deep">Leaflets</h3>
        <div className="text-xs text-moj-charcoal/60">
          {leaflets.length} total · only leaflets with pages are shown on the public gallery
        </div>
      </div>

      {isLoading && <div className="text-sm text-moj-charcoal/60">Loading leaflets…</div>}

      {!isLoading && leaflets.length === 0 && (
        <div className="card-moj p-8 text-center text-moj-charcoal/70" data-testid="leaflets-empty">
          <BookOpen size={28} className="mx-auto mb-2 text-moj-purple-deep/40" />
          No leaflets yet. Create one above to get started.
        </div>
      )}

      <div className="space-y-3">
        {leaflets.map((l) => (
          <LeafletRow
            key={l.id}
            leaflet={l}
            isOpen={openId === l.id}
            onToggle={() => setOpenId((cur) => (cur === l.id ? null : l.id))}
            onDelete={deleteMutation.mutate}
            onChanged={() => refetch()}
          />
        ))}
      </div>
    </div>
  );
}
